/**
 * Onboarding redesign — Screen 3 "live mascot" gear toggles (pure logic, no I/O).
 *
 * Reuses the real Character Customizer catalog + equip rules so the demo mascot is dressed
 * from the same layer system as the app (via LayeredAvatarView). Removable gear (cap/scarf/
 * sleeves) toggles on/off; boots are non-removable in our catalog, so their toggle CYCLES a
 * colour and never leaves the mascot barefoot.
 */

import {
  normalizeEquipped,
  toggleEquip,
} from '../../../components/avatars/LayeredAvatarLab/characterCustomizer.helper';
import type {
  CustomizerCategoryId,
  EquippedMap,
} from '../../../components/avatars/LayeredAvatarLab/characterCustomizer.types';

export interface OnboardingGear {
  key: string;
  label: string;
  cat: CustomizerCategoryId;
  /** Removable gear: the catalog item id toggled on/off. */
  itemId?: string;
  /** Non-removable gear (boots): tap cycles through these ids; never empties. */
  cycle?: string[];
}

/** The look the mascot opens with — all four gears equipped so the toggles read as "on". */
export const DEFAULT_ONBOARDING_FIT: EquippedMap = normalizeEquipped({
  base: 'striker',
  hair: null,
  hat: 'red',
  scarf: 'yellowblack',
  sleeves: 'black',
  pants: 'black',
  shoes: 'yellow',
});

/** The 2×2 gear grid on Screen 3 (mock's cap/scarf/gloves/boots → our cap/scarf/shorts/boots). */
export const ONBOARDING_GEAR: OnboardingGear[] = [
  { key: 'hat', label: 'Cap', cat: 'hat', itemId: 'red' },
  { key: 'scarf', label: 'Scarf', cat: 'scarf', itemId: 'yellowblack' },
  { key: 'pants', label: 'Shorts', cat: 'pants', itemId: 'black' },
  { key: 'boots', label: 'Boots', cat: 'shoes', cycle: ['yellow', 'bluewhite', 'pink', 'purple'] },
];

/** Whether a gear reads as "on" in the toggle grid (boots are always worn). */
export function isGearActive(equipped: EquippedMap, gear: OnboardingGear): boolean {
  if (gear.cycle) return true;
  return equipped[gear.cat] != null;
}

/**
 * Toggle one gear on the equipped look. Removable → on/off; boots → next colour in the cycle.
 * Always routed through `normalizeEquipped`/`toggleEquip` so base + hair/hat exclusivity hold.
 */
export function toggleOnboardingGear(equipped: EquippedMap, gear: OnboardingGear): EquippedMap {
  if (gear.cycle && gear.cycle.length > 0) {
    const current = equipped[gear.cat];
    const idx = current ? gear.cycle.indexOf(current) : -1;
    const next = gear.cycle[(idx + 1) % gear.cycle.length];
    return normalizeEquipped({ ...equipped, [gear.cat]: next });
  }
  return toggleEquip(equipped, gear.cat, gear.itemId ?? null);
}
